import { useCallback, useEffect, useRef, useState } from "react";
import { Any } from "@cede/types";

export const useDebounceFn = <T extends Any[]>(fn: (...args: T) => void, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const callbackRef = useRef(fn);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    callbackRef.current = fn;
  }, [fn]);

  const debouncedFn = useCallback(
    (...args: T) => {
      setIsDebouncing(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      const cb = callbackRef.current;
      timeoutRef.current = setTimeout(() => {
        cb(...args);
        setIsDebouncing(false);
      }, delay);
    },
    [delay],
  );

  return { debouncedFn, isDebouncing };
};

export const useDebounceValue = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    setIsDebouncing(true);
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
      setIsDebouncing(false);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delay]);

  return { debouncedValue, isDebouncing };
};
